import { useId, useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { MetricSeries } from '../api/types'
import { formatAxisTick, formatDate, formatMetric, metricLabel } from '../format'

interface Props {
  series: MetricSeries
  comparisonLabel: string
}

interface Row {
  date: string
  comparisonDate: string
  /** Null on a still-settling day, so the settled line stops rather than dipping. */
  current: number | null
  /**
   * The same measure on days GA4 has not finished counting, drawn as its own dashed
   * line. Two keys rather than one styled conditionally because a line is styled
   * whole — and they overlap on the boundary day so the two join instead of leaving a
   * visible break where nothing actually happened.
   */
  settling: number | null
  /**
   * The baseline, which runs the whole length of the chart — through the settling days
   * too, where it is a *complete* day against a part-day. That asymmetry is the point
   * of looking: the gap closes as the day fills in. Null only when no baseline was
   * fetched for that date, which is not the same as a measured zero.
   */
  comparison: number | null
  provisional: boolean
}

/**
 * Current period against its comparison, on a single y-axis.
 *
 * Both lines plot the same measure, so one scale is correct and a second axis would
 * misstate the relationship. The comparison series is muted and dashed: it is a
 * baseline rather than a second entity, and the dash keeps the two distinguishable
 * without relying on hue.
 */
export function TrendChart({ series, comparisonLabel }: Props) {
  const [showTable, setShowTable] = useState(false)
  const tableId = useId()

  const firstProvisional = series.points.findIndex((point) => point.provisional)
  // The last settled day, which both lines carry so they meet there.
  const joinIndex = firstProvisional > 0 ? firstProvisional - 1 : -1

  const rows: Row[] = series.points.map((point, index) => ({
    date: point.date,
    comparisonDate: point.comparison_date,
    current: point.provisional ? null : point.value,
    settling: point.provisional || index === joinIndex ? point.value : null,
    comparison: point.comparison_available === false ? null : point.comparison_value,
    provisional: point.provisional === true,
  }))

  const provisionalRows = rows.filter((row) => row.provisional)
  const label = metricLabel(series.metric)
  const hasData = rows.some((row) => row.current || row.settling || row.comparison)

  return (
    <div className="chart-card">
      <div className="chart-card__head">
        <h3 className="chart-card__title">{label}</h3>
        <div className="legend">
          <span className="legend__item">
            <span className="legend__key" style={{ borderTopColor: 'var(--series-current)' }} />
            Selected period
          </span>
          <span className="legend__item">
            <span
              className="legend__key"
              style={{ borderTopColor: 'var(--series-comparison)', borderTopStyle: 'dashed' }}
            />
            {comparisonLabel}
          </span>
          {provisionalRows.length > 0 && (
            <span className="legend__item">
              <span
                className="legend__key"
                style={{ borderTopColor: 'var(--series-current)', borderTopStyle: 'dashed' }}
              />
              Still consolidating
            </span>
          )}
        </div>
      </div>

      {hasData ? (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={rows} margin={{ top: 10, right: 16, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="var(--grid)" strokeWidth={1} vertical={false} />
            {/* Behind the grid-facing marks but under both lines: the band says "GA4 is
                still counting these days", so it has to cover the region rather than
                decorate the line. It starts on the join day so the shading begins where
                the settled data ends, not one day late. */}
            {provisionalRows.length > 0 && joinIndex >= 0 && (
              <ReferenceArea
                x1={rows[joinIndex].date}
                x2={provisionalRows[provisionalRows.length - 1].date}
                fill="var(--series-provisional)"
                stroke="none"
                ifOverflow="extendDomain"
              />
            )}
            <XAxis
              dataKey="date"
              tickFormatter={(value: string) => formatDate(value)}
              stroke="var(--axis)"
              tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: 'var(--axis)' }}
              minTickGap={18}
            />
            <YAxis
              tickFormatter={(value: number) => formatAxisTick(series.metric, value)}
              stroke="var(--axis)"
              tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              width={56}
            />
            <Tooltip
              content={<TrendTooltip metric={series.metric} comparisonLabel={comparisonLabel} />}
              cursor={{ stroke: 'var(--axis)', strokeWidth: 1 }}
            />
            {/* Comparison first so the current period draws on top of it. */}
            <Line
              type="monotone"
              dataKey="comparison"
              stroke="var(--series-comparison)"
              strokeWidth={2}
              strokeDasharray="5 4"
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface)' }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="current"
              stroke="var(--series-current)"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface)' }}
              isAnimationActive={false}
            />
            {/* The still-settling tail: same hue, because it is the same measure, but
                dashed and over a shaded band so it cannot be read as a finished figure.
                Drawn last so it sits above the band. */}
            {provisionalRows.length > 0 && (
              <Line
                type="monotone"
                dataKey="settling"
                stroke="var(--series-current)"
                strokeWidth={2}
                strokeDasharray="3 3"
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface)' }}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <p className="placeholder">No {label.toLowerCase()} recorded in this window.</p>
      )}

      <p style={{ margin: '4px 0 12px' }}>
        <button
          type="button"
          className="table-toggle"
          onClick={() => setShowTable((open) => !open)}
          aria-expanded={showTable}
          aria-controls={tableId}
        >
          {showTable ? 'Hide values' : 'Show values'}
        </button>
      </p>

      {showTable && (
        <div className="table-wrap" id={tableId}>
          <table>
            <caption className="visually-hidden">
              {label} for the selected period and {comparisonLabel}
            </caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Selected</th>
                <th scope="col">{comparisonLabel}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.date}>
                  <th scope="row">
                    {formatDate(row.date, 'long')}
                    {/* The shading carries this on the chart; the table has to say it in
                        words, or a screen reader gets a settled-looking number. */}
                    {row.provisional && (
                      <span className="funnel__cell-rate"> still consolidating, vs a full day</span>
                    )}
                  </th>
                  <td>
                    {formatMetric(series.metric, (row.provisional ? row.settling : row.current) ?? 0, 'full')}
                  </td>
                  <td>{row.comparison === null ? '—' : formatMetric(series.metric, row.comparison, 'full')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

interface TooltipProps {
  active?: boolean
  payload?: { payload: Row }[]
  metric: string
  comparisonLabel: string
}

function TrendTooltip({ active, payload, metric, comparisonLabel }: TooltipProps) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload
  const value = row.provisional ? row.settling : row.current

  return (
    <div className="tooltip">
      <div className="tooltip__date">{formatDate(row.date, 'long')}</div>
      <div className="tooltip__row">
        <span className="tooltip__name">
          <span
            className="legend__key"
            style={{
              borderTopColor: 'var(--series-current)',
              borderTopStyle: row.provisional ? 'dashed' : 'solid',
            }}
          />
          {row.provisional ? 'So far' : 'Selected'}
        </span>
        <span className="tooltip__value">{formatMetric(metric, value ?? 0, 'full')}</span>
      </div>

      <div className="tooltip__row">
        <span className="tooltip__name">
          <span
            className="legend__key"
            style={{ borderTopColor: 'var(--series-comparison)', borderTopStyle: 'dashed' }}
          />
          {comparisonLabel}
        </span>
        <span className="tooltip__value">
          {row.comparison === null ? '—' : formatMetric(metric, row.comparison, 'full')}
        </span>
      </div>
      <div className="tooltip__row" style={{ marginTop: 4 }}>
        <span className="tooltip__name" style={{ fontSize: '0.75rem' }}>
          {formatDate(row.comparisonDate, 'long')}
          {/* On a settling day the two sides are not like for like — a part-day against
              a whole one — so the tooltip says so at the moment of comparison rather
              than leaving the shading to carry it alone. */}
          {row.provisional && ' · a full day'}
        </span>
      </div>

      {row.provisional && (
        <div className="tooltip__row" style={{ marginTop: 4 }}>
          <span className="tooltip__name" style={{ fontSize: '0.75rem' }}>
            Still consolidating — this figure only rises
          </span>
        </div>
      )}
    </div>
  )
}
